// Forgiving in-memory text match for the Activities Explore search. The
// activity list is family-sized, so we just score every candidate on each
// keystroke — no index needed. Trigram Dice similarity forgives typos
// ("musuem" → "museum"), with a substring boost so exact-ish matches always
// win. Hand-rolled: a fuzzy-search library can't earn its place for a list of
// dozens.

// Lowercase, fold every non-alphanumeric run to a single space, trim. Keeps
// "Riverside Park, Leeds" and "riverside park leeds" comparable. Apostrophes
// are dropped, not spaced, so possessives match unquoted — "Mum's" → "mums".
const normalize = (s: string): string =>
	s
		.toLowerCase()
		.replace(/['’]/g, '')
		.replace(/[^a-z0-9]+/g, ' ')
		.trim()

// Padded 3-grams of a normalized string. The leading/trailing pad weights word
// starts, which helps short prefix queries.
const trigramSet = (s: string): Set<string> => {
	const padded = `  ${s} `
	const grams = new Set<string>()
	for (let i = 0; i < padded.length - 2; i++) {
		grams.add(padded.slice(i, i + 3))
	}
	return grams
}

// Sørensen–Dice coefficient of two trigram sets: 2·|shared| / (|a|+|b|).
const dice = (a: Set<string>, b: Set<string>): number => {
	if (a.size === 0 || b.size === 0) return 0
	let shared = 0
	for (const g of a) if (b.has(g)) shared++
	return (2 * shared) / (a.size + b.size)
}

// Keep results scoring at or above this; tuned so typo-level matches survive
// while unrelated names drop out.
export const MATCH_THRESHOLD = 0.3

// 0 (no match) … 1 (exact). A normalized-substring hit short-circuits to a high
// score, nudged toward 1 the earlier it appears, so "park" ranks "Park Cafe"
// above a merely trigram-similar name. Otherwise fall back to trigram Dice
// similarity. Below 3 chars trigrams are unreliable, so a short query only
// matches via the substring path.
export const searchScore = (query: string, target: string): number => {
	const q = normalize(query)
	const t = normalize(target)
	if (q === '' || t === '') return 0

	const at = t.indexOf(q)
	if (at !== -1) {
		const positionBonus = 1 - at / t.length
		return 0.7 + 0.3 * positionBonus
	}

	if (q.length < 3) return 0

	return dice(trigramSet(q), trigramSet(t))
}

// Best score across several fields (e.g. an activity's name and place label) —
// max, not concat, so a long secondary field can't dilute a strong name match.
export const bestScore = (
	query: string,
	fields: ReadonlyArray<string>,
): number =>
	fields.reduce((best, f) => Math.max(best, searchScore(query, f)), 0)
